import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isAuthenticated, getCurrentUser, logout, isCompanyUser } from '../services/api';
import LoginModal from './loginModal';
import RegisterModal from './registerModal';
import CompanyRegisterModal from './companyRegisterModal';
import CompanyLoginModal from './companyLoginModal';
import logo from '../logo.png';
import './navbar.css';

export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  }
  return ComponentWithRouterProp;
}

export default function Navbar(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const toggleLang = () => {
    const next = i18n.language === 'it' ? 'en' : 'it';
    i18n.changeLanguage(next);
    localStorage.setItem('happy_lang', next);
  };

  const [modals, setModals] = useState({
    login: false, register: false, companyLogin: false, companyRegister: false
  });
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [isCompany, setIsCompany] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    if (isLoggedIn) {
      isCompanyUser().then(v => setIsCompany(v));
    }
  }, [isLoggedIn]);

  const openModal = (key) => {
    setModals({ login: false, register: false, companyLogin: false, companyRegister: false, [key]: true });
    setMenuOpen(false);
  };
  const closeAll = () => setModals({ login: false, register: false, companyLogin: false, companyRegister: false });

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setIsCompany(false);
    navigate('/');
    setMenuOpen(false);
  };

  const updateLoginState = () => {
    const loggedIn = isAuthenticated();
    setIsLoggedIn(loggedIn);
    setCurrentUser(getCurrentUser());
    if (loggedIn) isCompanyUser().then(v => setIsCompany(v));
    else setIsCompany(false);
  };

  const handleAbout = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' });
    }
    if (props.onAboutClick) props.onAboutClick();
  };

  const handleContact = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => window.dispatchEvent(new CustomEvent('showContactSection')), 300);
    } else {
      window.dispatchEvent(new CustomEvent('showContactSection'));
    }
  };

  const handleScrollToEvents = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById('events-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.getElementById('events-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isHome = location.pathname === '/';

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!currentUser) return '?';
    const name = currentUser.username || currentUser.email || '';
    return name.charAt(0).toUpperCase();
  };

  const navLinks = [
    { label: t('nav.home'), action: () => navigate('/'), isActive: isHome },
    { label: t('nav.events'), action: handleScrollToEvents, isActive: false },
    { label: t('nav.about'), action: handleAbout, isActive: false },
    { label: t('nav.contact'), action: handleContact, isActive: false },
  ];

  return (
    <>
      <nav className={`nav-root${scrolled ? ' scrolled' : ''}`}>
        <Container>
          <div className="nav-inner">
            {/* Logo */}
            <div className="nav-logo" onClick={() => { navigate('/'); setMenuOpen(false); }}>
              <img src={logo} alt="EventHappy" />
            </div>

            {/* Desktop nav links */}
            <div className="nav-links d-none d-lg-flex">
              {navLinks.map(link => (
                <button
                  key={link.label}
                  className={`nav-link${link.isActive ? ' active' : ''}`}
                  onClick={link.action}
                >
                  {link.label}
                </button>
              ))}
              <button
                className="nav-link nav-link-aziende"
                onClick={() => openModal('companyLogin')}
              >
                ⚡ {t('nav.companies')}
              </button>
            </div>

            {/* Desktop actions */}
            <div className="nav-actions d-none d-lg-flex">
              {isLoggedIn ? (
                <>
                  <button
                    className="nav-user-chip"
                    onClick={() => navigate(isCompany ? '/company-dashboard' : '/user-profile')}
                  >
                    <div className="nav-user-avatar">{getUserInitials()}</div>
                    {isCompany ? t('nav.dashboard') : t('nav.profile')}
                  </button>
                  <button className="nav-btn-logout" onClick={handleLogout}>{t('nav.logout')}</button>
                </>
              ) : (
                <>
                  <button className="nav-btn-ghost" onClick={() => openModal('login')}>
                    {t('nav.login')}
                  </button>
                  <button className="nav-btn-primary" onClick={() => openModal('register')}>
                    {t('nav.register')}
                  </button>
                </>
              )}
              <button
                onClick={toggleLang}
                style={{ background: 'none', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 20, padding: '3px 10px', color: '#a78bfa', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', marginLeft: '0.5rem' }}
                title="Switch language"
              >
                {i18n.language === 'it' ? '🇬🇧 EN' : '🇮🇹 IT'}
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              className="nav-hamburger d-lg-none"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Chiudi menu' : 'Apri menu'}
            >
              <div className={`hamburger-icon${menuOpen ? ' open' : ''}`}>
                <span />
                <span />
                <span />
              </div>
            </button>
          </div>
        </Container>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="nav-mobile-menu">
            <Container>
              <div className="nav-mobile-links">
                {navLinks.map(link => (
                  <button
                    key={link.label}
                    className={`nav-mobile-link${link.isActive ? ' active' : ''}`}
                    onClick={link.action}
                  >
                    {link.label}
                  </button>
                ))}
                <button className="nav-mobile-link" onClick={() => openModal('companyLogin')}>
                  ⚡ {t('nav.companies')}
                </button>
              </div>
              <hr className="nav-mobile-divider" />
              <div className="nav-mobile-actions">
                {isLoggedIn ? (
                  <>
                    <button
                      className="nav-mobile-link"
                      onClick={() => { navigate(isCompany ? '/company-dashboard' : '/user-profile'); setMenuOpen(false); }}
                    >
                      {isCompany ? `⚡ ${t('nav.dashboard')}` : `👤 ${t('nav.profile')}`}
                    </button>
                    <button className="nav-btn-ghost" onClick={handleLogout}>{t('nav.logout')}</button>
                  </>
                ) : (
                  <>
                    <button className="nav-btn-ghost" onClick={() => openModal('login')}>{t('nav.login')}</button>
                    <button className="nav-btn-primary" onClick={() => openModal('register')}>{t('nav.register')}</button>
                  </>
                )}
                <button
                  onClick={toggleLang}
                  style={{ background: 'none', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 20, padding: '5px 14px', color: '#a78bfa', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', marginTop: '0.5rem' }}
                >
                  {i18n.language === 'it' ? '🇬🇧 Switch to English' : '🇮🇹 Passa all\'Italiano'}
                </button>
              </div>
            </Container>
          </div>
        )}
      </nav>

      {/* Backdrop */}
      {menuOpen && <div className="nav-mobile-backdrop" onClick={() => setMenuOpen(false)} />}

      <LoginModal show={modals.login} onHide={closeAll}
        onSwitchToRegister={() => openModal('register')}
        onSwitchToCompanyRegister={() => openModal('companyRegister')}
        onLoginSuccess={() => { closeAll(); updateLoginState(); }} />

      <RegisterModal show={modals.register} onHide={closeAll}
        onSwitchToLogin={() => openModal('login')}
        onSwitchToCompanyRegister={() => openModal('companyRegister')} />

      <CompanyRegisterModal show={modals.companyRegister} onHide={closeAll}
        onSwitchToLogin={() => openModal('companyLogin')} />

      <CompanyLoginModal show={modals.companyLogin} onHide={closeAll}
        onSwitchToRegister={() => openModal('companyRegister')}
        onSwitchToLogin={() => openModal('login')}
        onLoginSuccess={() => { closeAll(); updateLoginState(); }} />
    </>
  );
}
